import React, { useState, useEffect } from 'react';
import { X, DollarSign, CreditCard, Building, AlertTriangle } from 'lucide-react';
import NumberKeypad from './NumberKeypad';

function PaymentModal({
  open,
  onClose,
  cashAmount,
  fastmaxTransferAmount,
  providerTransferAmount,
  onCashChange,
  onFastmaxChange,
  onProviderChange,
  deliveryCharge
}) {
  const [activeField, setActiveField] = useState('cash');
  const [showWarning, setShowWarning] = useState(false);
  
  // Track string inputs for each field
  const [cashInput, setCashInput] = useState('0');
  const [fastmaxInput, setFastmaxInput] = useState('0');
  const [providerInput, setProviderInput] = useState('0');

  // Sync input strings with parent state values when modal opens
  useEffect(() => {
    if (open) {
      setCashInput(cashAmount.toString());
      setFastmaxInput(fastmaxTransferAmount.toString());
      setProviderInput(providerTransferAmount.toString());
    }
  }, [open, cashAmount, fastmaxTransferAmount, providerTransferAmount]);

  if (!open) return null;

  const totalEntered = cashAmount + fastmaxTransferAmount + providerTransferAmount;
  const remaining = deliveryCharge - totalEntered;
  const amountMatches = Math.abs(remaining) < 0.01;

  const getCurrentInput = () => {
    if (activeField === 'cash') return cashInput;
    if (activeField === 'fastmax') return fastmaxInput;
    return providerInput;
  };

  const setCurrentInput = (value) => {
    if (activeField === 'cash') {
      setCashInput(value);
      onCashChange(parseFloat(value) || 0);
    } else if (activeField === 'fastmax') {
      setFastmaxInput(value);
      onFastmaxChange(parseFloat(value) || 0);
    } else {
      setProviderInput(value);
      onProviderChange(parseFloat(value) || 0);
    }
  };

  const handleNumberClick = (num) => {
    const currentInput = getCurrentInput();
    
    // Handle decimal point
    if (num === '.') {
      // Only add decimal if there isn't one already
      if (!currentInput.includes('.')) {
        setCurrentInput(currentInput + '.');
      }
      return;
    }

    // Handle number input
    let newInput;
    if (currentInput === '0') {
      newInput = num;
    } else {
      newInput = currentInput + num;
    }
    
    setCurrentInput(newInput);
  };

  const handleDelete = () => {
    const currentInput = getCurrentInput();
    
    if (currentInput.length <= 1) {
      setCurrentInput('0');
    } else {
      const newInput = currentInput.slice(0, -1);
      setCurrentInput(newInput);
    }
  };

  const handleClear = () => {
    setCurrentInput('0');
  };

  const handleDone = () => {
    if (amountMatches) {
      onClose();
    } else {
      setShowWarning(true);
    }
  };

  const handleConfirmWarning = () => {
    setShowWarning(false);
    onClose();
  };

  const handleCancelWarning = () => {
    setShowWarning(false);
  };

  return (
    <>
      {/* Main Payment Modal - z-index 2000 */}
      <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 2000 }}>
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />
        <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto relative">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
            <h2 className="text-xl font-bold text-gray-900">Cobro</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Monto a cobrar:</span>
                <span className="font-bold text-gray-900">S/ {deliveryCharge.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total ingresado:</span>
                <span className={`font-bold ${
                  amountMatches
                    ? 'text-brand-green' 
                    : totalEntered > deliveryCharge 
                    ? 'text-orange-600' 
                    : 'text-brand-primary'
                }`}>
                  S/ {totalEntered.toFixed(2)}
                </span>
              </div>
              {!amountMatches && (
                <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                  <span className="text-gray-600">{remaining > 0 ? 'Falta:' : 'Exceso:'}</span>
                  <span className={`font-bold ${remaining > 0 ? 'text-orange-600' : 'text-red-600'}`}>
                    S/ {Math.abs(remaining).toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            {/* Payment Fields - 3 Columns */}
            <div className="grid grid-cols-3 gap-2">
              {/* Cash */}
              <button
                onClick={() => setActiveField('cash')}
                className={`p-3 rounded-lg border-2 transition-all ${
                  activeField === 'cash'
                    ? 'border-brand-primary bg-brand-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <div className={`p-2 rounded-lg ${
                    activeField === 'cash' ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-600'
                  }`}>
                    <DollarSign className="w-5 h-5" />
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-medium text-gray-600">Efectivo</p>
                    <p className="text-sm font-bold text-gray-900">S/ {cashInput}</p>
                  </div>
                </div>
              </button>

              {/* Fast Max Transfer */}
              <button
                onClick={() => setActiveField('fastmax')}
                className={`p-3 rounded-lg border-2 transition-all ${
                  activeField === 'fastmax'
                    ? 'border-brand-primary bg-brand-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <div className={`p-2 rounded-lg ${
                    activeField === 'fastmax' ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-600'
                  }`}>
                    <CreditCard className="w-5 h-5" />
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-medium text-gray-600">Fast Max</p>
                    <p className="text-sm font-bold text-gray-900">S/ {fastmaxInput}</p>
                  </div>
                </div>
              </button>

              {/* Provider Transfer */}
              <button
                onClick={() => setActiveField('provider')}
                className={`p-3 rounded-lg border-2 transition-all ${
                  activeField === 'provider'
                    ? 'border-brand-primary bg-brand-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <div className={`p-2 rounded-lg ${
                    activeField === 'provider' ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-600'
                  }`}>
                    <Building className="w-5 h-5" />
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-medium text-gray-600">Proveedor</p>
                    <p className="text-sm font-bold text-gray-900">S/ {providerInput}</p>
                  </div>
                </div>
              </button>
            </div>

            {/* Number Keypad */}
            <NumberKeypad
              onNumberClick={handleNumberClick}
              onDelete={handleDelete}
              onClear={handleClear}
            />

            {/* Done Button */}
            <button
              onClick={handleDone}
              className="w-full bg-brand-primary text-white py-3 rounded-lg font-semibold hover:bg-brand-primary/90 transition-all"
            >
              Listo
            </button>
          </div>
        </div>
      </div>

      {/* Warning Modal - z-index 3000 (higher than payment modal) */}
      {showWarning && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 3000 }}>
          <div className="absolute inset-0 bg-black/70" onClick={handleCancelWarning} />
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 space-y-4 relative">
            {/* Warning Icon */}
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-orange-600" />
              </div>
            </div>

            {/* Title */}
            <h3 className="text-xl font-bold text-gray-900 text-center">
              Monto no coincide
            </h3>

            {/* Message */}
            <div className="space-y-2 text-center">
              <p className="text-gray-600">
                El monto ingresado <span className="font-bold text-gray-900">S/ {totalEntered.toFixed(2)}</span> no coincide con el monto a cobrar <span className="font-bold text-gray-900">S/ {deliveryCharge.toFixed(2)}</span>
              </p>
              <p className="text-sm text-gray-500">
                {remaining > 0 
                  ? `Falta S/ ${Math.abs(remaining).toFixed(2)}`
                  : `Hay un exceso de S/ ${Math.abs(remaining).toFixed(2)}`
                }
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleCancelWarning}
                className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-all"
              >
                Corregir
              </button>
              <button
                onClick={handleConfirmWarning}
                className="flex-1 bg-brand-primary text-white py-3 rounded-lg font-semibold hover:bg-brand-primary/90 transition-all"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PaymentModal;